import type { OptimizationGroupModel } from '@/shared/generated'
import type {
	EnrichedScenario,
	GroupedScenario,
	OptimizationGroupMap,
	ScenarioWorkspaceItemPanel,
} from '../model/types'

import React, {
	createContext,
	use,
	useCallback,
	useMemo,
	useState,
} from 'react'
import { keyBy } from 'lodash/fp'
import { useDebounceValue } from 'usehooks-ts'

import { useOptimizationGroupsQuery } from '@/entities/optimization-group'
import { useScenarioQueryListQuery } from '@/entities/scenario'

import { useMutationObservers } from '@/shared/lib/useMutationObservers'

import { enrichScenarios } from '../lib/enrichScenarios'
import { filterScenarios } from '../lib/filterScenarios'
import { getGroupedScenarios } from '../lib/getGroupedScenarios'
import { usePanelsStore } from '../lib/usePanelsStore'

type Panel = Pick<
	ScenarioWorkspaceItemPanel,
	'scenarioId' | 'pricingCampaignId' | 'part'
>
type ScenariosContextType = {
	groupedScenarios: GroupedScenario[]
	isLoading: boolean
	isError: boolean
	panels: ScenarioWorkspaceItemPanel[]
	updatePanel: (id: string, data: Partial<ScenarioWorkspaceItemPanel>) => void
	addPanel: (panel?: Panel) => void
	removePanel: (id: string) => void
	searchText: string
	setSearchText: React.Dispatch<React.SetStateAction<string>>
	isAllGroupsCollapsed: boolean
	handleToggleAll: () => void
	replaceLastPanel: (panel: Panel) => void
	isGroupOpen: (id: string) => boolean
	setOpenGroups: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
	scenarios: EnrichedScenario[]
	setOptimizationGroupIds: React.Dispatch<React.SetStateAction<string[]>>
	optimizationGroupIds: string[]
	selectedScenarioIds: EnrichedScenario['id'][]
	toggleScenarioSelection: (scenarioId: string) => void
	setSelectedScenarioIds: React.Dispatch<
		React.SetStateAction<EnrichedScenario['id'][]>
	>
}

export const ScenariosContext = createContext<ScenariosContextType>({
	groupedScenarios: [],
	isLoading: false,
	isError: false,
	panels: [],
	updatePanel: () => {},
	addPanel: () => {},
	removePanel: () => {},
	replaceLastPanel: () => {},
	searchText: '',
	setSearchText: () => {},
	isAllGroupsCollapsed: false,
	handleToggleAll: () => {},
	isGroupOpen: () => false,
	setOpenGroups: () => {},
	scenarios: [],
	setOptimizationGroupIds: () => {},
	optimizationGroupIds: [],
	selectedScenarioIds: [],
	setSelectedScenarioIds: () => {},
	toggleScenarioSelection: () => {},
})

export function ScenariosProvider({ children }: { children: React.ReactNode }) {
	const { panels, updatePanel, addPanel, removePanel } = usePanelsStore()
	/**
	 * Preselected scenario ids for run what-if
	 */
	const [selectedScenarioIds, setSelectedScenarioIds] = useState<
		EnrichedScenario['id'][]
	>([])
	const [search, setSearch] = useState('')
	const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({})
	const [isAllGroupsCollapsed, setIsAllGroupsCollapsed] =
		useState<boolean>(true)
	const [optimizationGroupIds, setOptimizationGroupIds] = useState<string[]>([])
	const [debouncedSearch] = useDebounceValue(search, 500)

	const handleToggleAll = useCallback(() => {
		setIsAllGroupsCollapsed((prev) => !prev)
		setOpenGroups({})
	}, [])

	const toggleScenarioSelection = useCallback((scenarioId: string) => {
		setSelectedScenarioIds((prev) =>
			prev.includes(scenarioId)
				? prev.filter((id) => id !== scenarioId)
				: [...prev, scenarioId]
		)
	}, [])
	const isGroupOpen = useCallback(
		(key: string) => openGroups[key] ?? !isAllGroupsCollapsed,
		[openGroups, isAllGroupsCollapsed]
	)

	const replaceLastPanel = useCallback(
		(panel: Panel) => {
			const lastPanel = panels[panels?.length - 1]
			if (lastPanel) {
				updatePanel(lastPanel.id, panel)
			} else {
				addPanel(panel)
			}
		},
		[panels, addPanel, updatePanel]
	)
	const handelDeleteSuccess = useCallback(
		(variables: string[] | string) => {
			panels.forEach((panel) => {
				if (panel.scenarioId && variables.includes(panel.scenarioId)) {
					removePanel(panel.id)
				}
			})
		},
		[panels, removePanel]
	)
	useMutationObservers({
		mutationKey: ['scenario-delete'],
		onSuccess: handelDeleteSuccess,
	})
	const {
		data: optimizationGroupMap,
		isLoading: isOptimizationGroupsLoading,
		isError: isOptimizationGroupsError,
	} = useOptimizationGroupsQuery<OptimizationGroupMap>({
		select: useCallback(
			(data: OptimizationGroupModel[]) =>
				keyBy('id')(data) as unknown as OptimizationGroupMap,
			[]
		),
	})
	const {
		data: rawScenarios,
		isLoading: isScenariosLoading,
		isError: isScenariosError,
	} = useScenarioQueryListQuery({
		optimization_group_ids: optimizationGroupIds.length
			? optimizationGroupIds
			: null,
	})

	const scenarios = useMemo(
		() => enrichScenarios(optimizationGroupMap)(rawScenarios ?? []),
		[optimizationGroupMap, rawScenarios]
	)

	const filteredScenarios = useMemo(
		() => filterScenarios(debouncedSearch, scenarios),
		[scenarios, debouncedSearch]
	)
	const groupedScenarios = useMemo(
		() => getGroupedScenarios(optimizationGroupMap)(filteredScenarios),
		[optimizationGroupMap, filteredScenarios]
	)

	const value = useMemo(
		() => ({
			groupedScenarios: groupedScenarios || [],
			scenarios: scenarios || [],
			isLoading: isOptimizationGroupsLoading || isScenariosLoading,
			isError: isOptimizationGroupsError || isScenariosError,
			panels,
			updatePanel,
			addPanel,
			removePanel,
			searchText: search,
			setSearchText: setSearch,
			isAllGroupsCollapsed,
			handleToggleAll,
			replaceLastPanel,
			isGroupOpen,
			setOpenGroups,
			setOptimizationGroupIds,
			optimizationGroupIds,
			selectedScenarioIds,
			setSelectedScenarioIds,
			toggleScenarioSelection,
		}),
		[
			groupedScenarios,
			isScenariosLoading,
			isScenariosError,
			isOptimizationGroupsLoading,
			isOptimizationGroupsError,
			panels,
			updatePanel,
			addPanel,
			removePanel,
			search,
			setSearch,
			isAllGroupsCollapsed,
			handleToggleAll,
			replaceLastPanel,
			isGroupOpen,
			openGroups,
			setOpenGroups,
			scenarios,
			setOptimizationGroupIds,
			optimizationGroupIds,
			openGroups,
			selectedScenarioIds,
			setSelectedScenarioIds,
			toggleScenarioSelection,
		]
	)

	return (
		<ScenariosContext value={value as ScenariosContextType}>
			{children}
		</ScenariosContext>
	)
}

export const usePanelController = (id: string) => {
	const context = use(ScenariosContext)

	if (!context) {
		throw new Error('useScenarios must be used within a ScenariosProvider')
	}

	return useMemo(
		() => ({
			panel: context.panels.find((panel) => panel.id === id),
			addPanel: (panel: ScenarioWorkspaceItemPanel) => context.addPanel(panel),
			removePanel: () => context.removePanel(id),
			updatePanel: (data: Partial<ScenarioWorkspaceItemPanel>) =>
				context.updatePanel(id, data),
		}),
		[context, id]
	)
}
export const useScenarios = () => {
	const context = use(ScenariosContext)
	if (!context) {
		throw new Error('useScenarios must be used within a ScenariosProvider')
	}

	return context
}
